/**
 * Iter 19.45 — Haftungsausschluss-Modal
 *
 * Wird vom Step-1-Consent-Checkbox angeklickt. Zeigt den vollständigen
 * Haftungsausschluss-Text in einem barrierearmen Modal. Inhalt darf 1:1
 * nicht verändert werden (rechtlich abgestimmter Text).
 */
import React, { useEffect, useRef } from "react";
import { X } from "@phosphor-icons/react";

import { Button } from "../components/ui/Button";

export default function HaftungsausschlussModal({ open, onClose }) {
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Trap Escape + Body-Scroll-Lock
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus close button so screen-readers announce + Esc works without click
    setTimeout(() => closeBtnRef.current?.focus(), 50);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="haftungsausschluss-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="haftungsausschluss-title"
      className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6 bg-ink-900/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl max-h-[90vh] bg-white border border-ink-200 rounded-sm shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-7 py-5 border-b border-ink-200 shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
              Rechtlicher Hinweis
            </div>
            <h2
              id="haftungsausschluss-title"
              className="font-cabinet text-2xl sm:text-3xl text-ink-900 tracking-tight font-medium"
            >
              Haftungsausschluss
            </h2>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="p-2 -mr-2 -mt-1 rounded-sm hover:bg-warm-100 text-warm-600 hover:text-ink-900 transition-crisp"
            data-testid="haftungsausschluss-modal-close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="overflow-y-auto px-7 py-6 text-[14px] leading-relaxed text-ink-800 space-y-4">
          <p>
            Die über das Einstufungstool (ET) bereitgestellten Ergebnisse
            zur Einstufung von Mitarbeiterinnen und Mitarbeitern in
            Beschäftigungs- bzw. Lohngruppen sowie zur Ermittlung von
            Vorrückungsstufen (auf Basis von anrechenbaren Vordienstzeiten)
            erfolgen auf Grundlage der von den Nutzerinnen und Nutzern
            eingegebenen Daten und Informationen. Das Einstufungstool (ET)
            übernimmt durch seine strukturierte Vorarbeit und das Vorfiltern
            relevanter Kriterien eine unterstützende Funktion (vergleichbar
            mit einer zuarbeitenden Assistenzkraft), wodurch den
            Nutzerinnen und Nutzern im Rahmen der Einstufungsbeurteilung
            erheblich Zeit und Aufwand erspart wird. Die Berechnungen und
            Vorschläge werden vom Einstufungstool (ET) nach bestem Wissen
            und Gewissen und mit größtmöglicher Sorgfalt erstellt. Dennoch
            kann keine Gewähr für die inhaltliche Richtigkeit oder
            rechtliche Verbindlichkeit der Ergebnisse übernommen werden.
            Insbesondere können kollektivvertragliche Sonderregelungen,
            betriebliche Vereinbarungen, individuelle Vertragsgestaltungen
            oder unvollständige bzw. fehlerhafte Eingaben zu abweichenden
            Ergebnissen führen.
          </p>
          <p>
            Das Einstufungstool (ET) stellt keine Rechtsberatung,
            Personalberatung oder verbindliche kollektivvertragliche
            Auskunft dar. Es kann und soll die erforderlichen Fachkenntnisse
            der Benutzer, die Heranziehung einschlägiger Rechtsprechung und
            Fachliteratur sowie die allenfalls empfehlenswerte Inanspruchnahme
            einer fachkundigen Beratung nicht ersetzen. Die endgültige
            Prüfung und Verantwortung für die korrekte Einstufung, Entlohnung,
            Vorrückung sowie die rechtliche Beurteilung liegt demnach
            ausschließlich bei der jeweiligen Nutzerin bzw. beim jeweiligen
            Nutzer. Eine Haftung der Betreiberin dieses Tools für allfällige
            Schäden oder sonstige Nachteile, die aus der Nutzung der
            bereitgestellten Ergebnisse entstehen, ist ausgeschlossen.
          </p>
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-ink-200 bg-warm-50/60 flex justify-end shrink-0">
          <Button
            variant="brand"
            size="lg"
            onClick={onClose}
            data-testid="haftungsausschluss-modal-ok"
          >
            Gelesen &amp; verstanden
          </Button>
        </div>
      </div>
    </div>
  );
}
